/**
 * Retries a dynamic import loader on transient chunk-load failures.
 *
 * Webpack/SWC chunk loads can fail due to flaky network conditions or
 * CDN hiccups. Retrying the import a small number of times with
 * exponential backoff lets the bundle recover instead of immediately
 * throwing into the nearest ErrorBoundary.
 */

interface RetryImportOptions {
  maxRetries?: number
  baseDelayMs?: number
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export async function retryImport<T>(
  loader: () => Promise<T>,
  { maxRetries = 3, baseDelayMs = 300 }: RetryImportOptions = {},
): Promise<T> {
  let lastError: unknown
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await loader()
    } catch (error) {
      lastError = error
      if (attempt === maxRetries) {
        break
      }
      const delay = baseDelayMs * 2 ** attempt
      await wait(delay)
    }
  }
  throw lastError
}
